import { z } from 'zod'

import { formatJalaliDateTime, formatToman, isAmountString } from '@/shared/lib'

/**
 * The error envelope every route shares (backend-routes.md §1).
 *
 * The client branches on `code` and displays `message_fa` verbatim. Never
 * parse `message_fa` — it is prose written for the user, not a contract.
 */
export const apiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message_fa: z.string(),
    detail: z.unknown().optional(),
  }),
})

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>

export class ApiError extends Error {
  readonly code: string
  readonly messageFa: string
  readonly detail: unknown
  readonly status: number

  constructor(status: number, code: string, messageFa: string, detail?: unknown) {
    super(`${code} (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.messageFa = messageFa
    this.detail = detail
  }

  /** `429` carries `retry_after_seconds` (backend-routes.md §1). */
  get retryAfterSeconds(): number | null {
    const detail = this.detail
    if (detail && typeof detail === 'object' && 'retry_after_seconds' in detail) {
      const value = (detail as { retry_after_seconds: unknown }).retry_after_seconds
      if (typeof value === 'number') return value
    }
    return null
  }
}

/** The network never answered — distinct from the backend answering with an error. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network unreachable')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

/**
 * The backend answered in a shape the contract does not describe. Surfaced
 * loudly rather than coerced, because a silently reshaped money field is worse
 * than a visible failure.
 */
export class ContractError extends Error {
  readonly issues: unknown

  constructor(path: string, issues: unknown) {
    super(`response for ${path} does not match the contract`)
    this.name = 'ContractError'
    this.issues = issues
  }
}

const FALLBACK_FA = 'خطایی رخ داد. لطفاً دوباره تلاش کنید.'

/* --- Persian for the codes the backend ships without copy -------------------
 *
 * `message_fa` is normally displayed verbatim and never parsed. But the service
 * has Persian for exactly **seven** codes; everything in its own `PENDING_COPY`
 * set — `forbidden`, `not_found`, `conflict`, `rate_limited`,
 * `insufficient_balance`, `market_halted`, `kill_switch_active`,
 * `validation_error`, `unauthenticated`, `provider_unavailable` — is answered
 * with one generic sentence, deliberately: `app/core/i18n.py` says writing new
 * user-facing Persian is a product decision it declines to make.
 *
 * So the rule stays "display what the server sent", with one exception: when
 * the server sent *that specific placeholder*, the client supplies something a
 * user can act on. `BACKEND_FALLBACK_FA` is the placeholder byte for byte; if
 * the backend ever writes real copy for a code, its message stops matching and
 * ours stops being used, with no change here.
 *
 * Some codes carry a `detail.reason` that names the actual cause, and a
 * refusal the user can fix ("your tier caps you") must not read the same as one
 * they cannot ("the market is halted"). Those are keyed `code:reason`.
 */
const BACKEND_FALLBACK_FA = 'خطایی رخ داد. لطفاً دوباره تلاش کنید.'

const MESSAGE_BY_CODE: Record<string, string> = {
  validation_error: 'اطلاعات واردشده معتبر نیست.',
  unauthenticated: 'برای ادامه باید وارد حساب خود شوید.',
  forbidden: 'این عملیات برای حساب شما مجاز نیست.',
  not_found: 'موردی با این مشخصات پیدا نشد.',
  conflict: 'این درخواست قبلاً ثبت شده است.',
  rate_limited: 'درخواست‌های شما بیش از حد مجاز است. کمی بعد دوباره تلاش کنید.',
  insufficient_balance: 'موجودی شما برای این عملیات کافی نیست.',
  market_halted: 'معامله این بازار در حال حاضر متوقف است.',
  kill_switch_active: 'معاملات موقتاً متوقف شده است.',
  provider_unavailable: 'سرویس طرف سوم در دسترس نیست. کمی بعد دوباره تلاش کنید.',
  precondition_failed: 'شرایط لازم برای این عملیات فراهم نیست.',
  // Toman withdrawal (§15). The first is an amount the fee would swallow;
  // the second one whose net no transfer type the bank offers will carry.
  amount_below_minimum: 'مبلغ از کارمزد برداشت کمتر است.',
  amount_below_transfer_minimum: 'حداقل مبلغ برداشت {min_irr} تومان است.',
  // `max_irr` is the net the bank will carry, not the amount typed.
  amount_above_transfer_maximum: 'مبلغ دریافتی از سقف انتقال بیشتر است؛ حداکثر {max_irr} تومان.',
  not_cancellable: 'این برداشت دیگر قابل لغو نیست.',
  // Core's own code, not a `conflict` reason: a key sent again with another
  // body. Of the live routes only the withdrawal form can meet it, so it
  // points there.
  idempotency_key_reuse: 'این درخواست قبلاً ثبت شده است؛ تاریخچهٔ برداشت‌ها را ببینید.',
}

const MESSAGE_BY_REASON: Record<string, string> = {
  // Trade and wallet limits — each names a different thing to do about it.
  'forbidden:daily_trade_limit': 'مبلغ درخواستی از سقف معامله روزانه سطح شما بیشتر است.',
  'forbidden:daily_withdraw_limit': 'مبلغ درخواستی از سقف برداشت روزانه سطح شما بیشتر است.',
  'forbidden:no_kyc_tier': 'برای این عملیات باید احراز هویت خود را تکمیل کنید.',
  'forbidden:banned': 'دسترسی این حساب مسدود شده است.',
  'forbidden:not_transferable': 'انتقال این دارایی امکان‌پذیر نیست.',
  // A session that ran out is not the same as one that never existed.
  'unauthenticated:expired': 'نشست شما منقضی شده است. دوباره وارد شوید.',
  'unauthenticated:password_required': 'برای این حساب ورود با رمز عبور لازم است.',
  // Market halts: the dealer's feed versus a deliberate pause.
  'market_halted:stale_feed': 'قیمت لحظه‌ای در دسترس نیست. کمی بعد دوباره تلاش کنید.',
  'market_halted:volatility_pause': 'به دلیل نوسان شدید، معامله این بازار موقتاً متوقف است.',
  'market_halted:kill_switch': 'معاملات موقتاً متوقف شده است.',
  'market_halted:no_tier_for_size': 'برای این مبلغ قیمتی ارائه نشده است. مبلغ دیگری را امتحان کنید.',
  'market_halted:market_inactive': 'این بازار در حال حاضر فعال نیست.',
  // The house cannot value this market in Rial right now — the quote asset's
  // own Rial feed is missing or stale, which halts every market quoted in it
  // at once. Not a fault of this market or of the amount, so it says neither.
  'market_halted:no_irr_cross_rate': 'قیمت‌گذاری این بازار موقتاً در دسترس نیست. کمی بعد دوباره تلاش کنید.',
  // Amount bounds.
  'validation_error:below_market_minimum': 'مبلغ واردشده از حداقل این بازار کمتر است.',
  'validation_error:above_market_maximum': 'مبلغ واردشده از حداکثر این بازار بیشتر است.',
  'validation_error:must_be_positive': 'مقدار باید بزرگ‌تر از صفر باشد.',
  'validation_error:below_fee': 'مبلغ واردشده از کارمزد کمتر است.',
  // Bank cards and identity.
  'conflict:card_already_registered': 'این کارت قبلاً ثبت شده است.',
  'conflict:iban_already_registered': 'این شبا قبلاً ثبت شده است.',
  'conflict:national_id_already_registered': 'این کد ملی قبلاً ثبت شده است.',
  'conflict:quote_already_accepted': 'این سفارش قبلاً ثبت شده است.',
  'conflict:document_already_submitted': 'این مدرک قبلاً ارسال شده است.',
  'conflict:document_not_rejected': 'فقط مدرک ردشده قابل ارسال مجدد است.',
  'forbidden:no_iban_to_verify': 'شبای این کارت هنوز استخراج نشده است.',
  // `/support/chat/session` while the chat is off. The generic not_found
  // sentence would tell someone asking for help that nothing was found.
  'not_found:support_chat_disabled': 'گفتگوی آنلاین فعلاً در دسترس نیست.',
  // The chat's bind, twice refused because the id Goftino issued was in use.
  // The generic conflict sentence says the request was already made.
  'conflict:chat_identity_taken': 'گفتگوی آنلاین آغاز نشد. لطفاً دوباره تلاش کنید.',
  // Toman withdrawal (§15): the account's own cooldown, a security change's
  // freeze, an operator's hold, and the surface switched off. The two times
  // come from the refusal's `detail`.
  'forbidden:bank_account_cooldown': 'این حساب از {withdrawable_at} قابل برداشت است.',
  'forbidden:withdrawal_freeze': 'برداشت تا {until} به‌دلیل تغییر امنیتی حساب ممکن نیست.',
  'forbidden:withdrawal_hold': 'برداشت از حساب شما موقتاً متوقف است. با پشتیبانی تماس بگیرید.',
  'forbidden:user_not_active': 'حساب شما فعال نیست.',
  'not_found:toman_withdrawal_disabled': 'برداشت تومان فعلاً در دسترس نیست.',
  // `/fiat/deposits/destination` with §11's flag off, which a screen opened
  // on a stale config meets. It sends the user back to the hub with this.
  'not_found:toman_deposit_disabled': 'واریز تومان فعلاً در دسترس نیست.',
}

/**
 * The figures a message names, read from the refusal's `detail` by the
 * placeholder's own name. A Rial amount is shown in Toman and a time in
 * Jalali, isolated left to right as the screens print one. A message whose
 * figure did not arrive is not shown half-filled; the next fallback is.
 */
const DETAIL_FORMAT: Record<string, (value: string) => string | null> = {
  min_irr: toman,
  max_irr: toman,
  withdrawable_at: jalaliTime,
  until: jalaliTime,
}

function toman(value: string): string | null {
  return isAmountString(value) ? formatToman(value) : null
}

function jalaliTime(value: string): string | null {
  if (Number.isNaN(Date.parse(value))) return null
  return `\u2066${formatJalaliDateTime(value)}\u2069`
}

function fill(template: string | undefined, detail: unknown): string | null {
  if (template === undefined) return null
  let complete = true
  const filled = template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const raw = stringField(detail, key)
    const value = raw === null ? null : (DETAIL_FORMAT[key]?.(raw) ?? null)
    if (value === null) complete = false
    return value ?? ''
  })
  return complete ? filled : null
}

/**
 * The reason the service named for a refusal, when it named one. Read only to
 * pick a message; never to reconstruct a decision the backend already made.
 */
function stringField(detail: unknown, key: string): string | null {
  if (detail && typeof detail === 'object' && key in detail) {
    const value = (detail as Record<string, unknown>)[key]
    if (typeof value === 'string') return value
  }
  return null
}

/**
 * Persian names for the fields a `validation_error` can point at.
 *
 * The service answers a rejected form field with `detail: {field: "..."}` and
 * no `reason`, so without this the user is told only that something was
 * invalid — on a form with six inputs.
 */
const FIELD_LABEL_FA: Record<string, string> = {
  postal_code: 'کد پستی',
  national_id: 'کد ملی',
  birth_date: 'تاریخ تولد',
  card_number: 'شماره کارت',
  iban: 'شبا',
  phone: 'شماره موبایل',
  password: 'رمز عبور',
  current_password: 'رمز فعلی',
  new_password: 'رمز جدید',
  amount: 'مبلغ',
  address: 'آدرس',
  document_id: 'مدرک',
}

/**
 * The Persian to show for one API error.
 *
 * Prefers the server's own words. Falls back to ours **only** when the server
 * sent its unwritten-copy placeholder.
 */
export function messageForCode(
  code: string,
  serverMessage: string,
  detail?: unknown,
): string {
  if (serverMessage !== BACKEND_FALLBACK_FA && serverMessage !== '') return serverMessage

  const reason = stringField(detail, 'reason')
  if (reason) {
    const specific = fill(MESSAGE_BY_REASON[`${code}:${reason}`], detail)
    if (specific) return specific
  }

  // A rejected form field is named rather than left to the user to find.
  const field = stringField(detail, 'field')
  if (field) {
    const label = FIELD_LABEL_FA[field]
    if (label) return `${label} واردشده معتبر نیست.`
  }

  return fill(MESSAGE_BY_CODE[code], detail) ?? FALLBACK_FA
}

export function toApiError(status: number, body: unknown): ApiError {
  const parsed = apiErrorBodySchema.safeParse(body)
  if (parsed.success) {
    const { code, message_fa, detail } = parsed.data.error
    return new ApiError(status, code, message_fa, detail)
  }
  return new ApiError(status, `http_${status}`, FALLBACK_FA, body)
}

/** Persian message for anything that reaches the UI, whatever its type. */
export function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    return messageForCode(error.code, error.messageFa, error.detail)
  }
  if (error instanceof NetworkError) return 'اتصال به اینترنت برقرار نیست.'
  if (error instanceof ContractError) return 'پاسخ سرور نامعتبر است.'
  return FALLBACK_FA
}
